import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Grid, Stack, TypographyProps } from '@mui/material';
import moment from 'moment';
import VisibilityIcon from '@mui/icons-material/Visibility';

import { IUser } from '../../../../store/UserSlice';
import { getAuthState } from '../../../../store/AuthSlice';
import { useAppSelector } from '../../../../hooks';
import DetailViewCard from '../../../../components/Card/DetailViewCard';
import CustomGrayBoxIconButton from '../../../../components/Button/CustomGrayBoxIconButton';
import { checkIsUser, getShortName } from '../../../../helper';

interface StudentListItemProps {
  student: IUser;
}

// STYLES
const titleProps: TypographyProps = {
  variant: 'caption',
  className: 'ellipsis-text',
  sx: {
    color: 'text.primary',
  },
};
const descriptionProps: TypographyProps = {
  className: 'ellipsis-text',
  variant: 'subtitle2',
  fontWeight: 600,
  sx: {
    color: 'text.primary',
  },
};

const StudentListItem: React.FC<StudentListItemProps> = ({ student }) => {
  // HOOKS
  const navigate = useNavigate();

  // STORE
  const authState = useAppSelector(getAuthState);

  // NAME
  let name = getShortName(
    student.firstname,
    student.lastname,
    !checkIsUser(authState.user)
  );
  if (name.trim() === '') {
    name = '-';
  }

  // MAIL / CREATED AT BY ROLE
  let roleByValue = student.email || '-';
  let roleByLabel = 'Email';

  if (
    authState.user &&
    authState.user.role &&
    authState.user.role.name &&
    authState.user.role.name === 'Tutor'
  ) {
    roleByValue = moment(student.createdAt).format('YYYY-MM-DD hh:mm A');
    roleByLabel = 'Joined At';
  }

  // LAST LOGIN
  let lastLogin = '-';
  if (student.lastLogin)
    lastLogin = moment(student.lastLogin).format('YYYY-MM-DD hh:mm A');

  return (
    <Card elevation={3} sx={{ mb: '15px' }}>
      <div style={{ padding: '15px' }}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <DetailViewCard
              title="Student Name"
              description={name}
              titleProps={titleProps}
              descriptionProps={descriptionProps}
              avatarProps={{
                src: student.avatar,
                alt: name,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <DetailViewCard
              title="User Name"
              description={student.username || '-'}
              titleProps={titleProps}
              descriptionProps={descriptionProps}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <DetailViewCard
              title={roleByLabel}
              description={roleByValue}
              titleProps={titleProps}
              descriptionProps={descriptionProps}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={4} lg={2}>
            <DetailViewCard
              title="Last Access"
              description={lastLogin}
              titleProps={titleProps}
              descriptionProps={descriptionProps}
            />
          </Grid>
          <Grid item xs={12} sm={2} md={2} lg={1}>
            <Stack direction="row" justifyContent="center" alignItems="center">
              <CustomGrayBoxIconButton
                onClick={() => {
                  navigate(
                    `${process.env.PUBLIC_URL}/dashboard/students/submissions/${student._id}`
                  );
                }}
              >
                <VisibilityIcon />
              </CustomGrayBoxIconButton>
            </Stack>
          </Grid>
        </Grid>
      </div>
    </Card>
  );
};

export default StudentListItem;
